import { useDispatch } from "react-redux";
import { UnitTable } from "./components/units/UnitTable";
import { UnitTableControls } from "./components/controls/UnitTableControls";
import { useEffect } from "react";
import { setApiKey, setEnvironment } from "./features/api/apiKeySlice";
import {
    DisplayOptionContext,
    defaultDisplayOption,
} from "@/context/DisplayOptionContext";
import { validateDisplayOptions } from "./lib/validations";
import { sortCategories } from "./utils/units/sortCategories";
import {
    getAvailableFeatures,
    getAvailableCategories,
} from "./utils/units/availableFilterOptions";
import { FEATURED_SORT_TOKEN } from "./utils/units/featuredUnits";
import { WidgetLayout } from "@/types/enums";
import type { UnitTableAppProps } from "./types";
import {
    GetUnitGroupsParams,
    useGetUnitGroupsQuery,
} from "./features/api/apiSlice";
import merge from "lodash/merge";
import { UnitJourneyWrapper } from "./components/journey/UnitJourneyWrapper";
import { LocationFilterToast } from "./components/controls/filter/LocationFilterToast";
import { AnnexLocationsMessage } from "./components/facility/AnnexLocationsMessage";

const App = ({ apiKey, environment, options, initialUnitGroups }: UnitTableAppProps) => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setApiKey(apiKey));
    }, [apiKey, dispatch]);

    useEffect(() => {
        if (environment) {
            dispatch(setEnvironment(environment));
        }
    }, [environment, dispatch]);

    const mergedOptions = merge({}, defaultDisplayOption, options);

    try {
        validateDisplayOptions(mergedOptions);
    } catch (error) {
        if (error instanceof Error) {
            console.error(error.message);
        } else {
            console.error("An unknown error occurred.");
        }
    }

    // Build query params - only include defined values
    const unitParams: GetUnitGroupsParams = {
        "facility-ids": mergedOptions.facilityIds.join(","),
    };

    if (mergedOptions.additionalUnitSourceInfo) {
        unitParams["unit-source"] = mergedOptions.additionalUnitSourceInfo;
    }
    if (mergedOptions.allowedFeatures) {
        unitParams.features = mergedOptions.allowedFeatures;
    }
    if (mergedOptions.allowedCategories) {
        unitParams.categories = mergedOptions.allowedCategories;
    }
    if (mergedOptions.sort) {
        // "featured" is not a Storage Essentials sort property — it is handled
        // client-side (pinning featured rows to the top), so strip it here.
        const apiSort = mergedOptions.sort
            .split(",")
            .filter((token) => token.split(":")[0]?.trim() !== FEATURED_SORT_TOKEN)
            .join(",");
        if (apiSort) {
            unitParams.sort = apiSort;
        }
    }
    if (mergedOptions.groupBy) {
        unitParams.group = mergedOptions.groupBy;
    }

    const {
        data: unitGroupsData,
        error: unitGroupsError,
        isLoading: unitGroupsLoading,
    } = useGetUnitGroupsQuery(unitParams, { skip: !apiKey });

    // Features/categories are only offered as filter options when an available
    // unit group has them (WEB-1518 / se-vapor PR #180).
    const availableFeatures = getAvailableFeatures(unitGroupsData?.data);
    const availableCategories = getAvailableCategories(unitGroupsData?.data);

    // Layout 2 displays categories in a configurable order; Layout 1 keeps API order.
    const orderedCategories =
        mergedOptions.widgetLayout === WidgetLayout.LAYOUT_2
            ? sortCategories(availableCategories, mergedOptions.categoryOrder)
            : availableCategories;

    // The market layout has no filter/sort UI (WEB-704): suppress the controls
    // entirely so the Layout-2 category-pill bar never renders in market mode.
    const isMarketLayout = mergedOptions.widgetLayout === WidgetLayout.MARKET;

    return (
        <DisplayOptionContext.Provider value={mergedOptions}>
            {!isMarketLayout && (
                <UnitTableControls
                    features={availableFeatures}
                    categories={orderedCategories}
                    categoriesLoading={unitGroupsLoading}
                />
            )}
            <AnnexLocationsMessage unitGroups={unitGroupsData?.data ?? []} />
            <UnitTable
                units={unitGroupsData?.data ?? []}
                unitsError={unitGroupsError}
                unitsIsLoading={unitGroupsLoading}
                initialUnitGroups={initialUnitGroups}
            />
            <UnitJourneyWrapper />
            <LocationFilterToast />
        </DisplayOptionContext.Provider>
    );
};

export default App;
